/**
 * MaterialSampleCard — Individual material sample row card
 *
 * DESIGN (matches Figma concept TrendKitDetailPage):
 *   Left:  chip image (240×240, contain, 12px padding, radius 10)
 *   Right: 52×52 square swatch + name + inline spec line + arrow CTA
 *   Hover: border turns blue, shadow deepens, bottom gradient bar reveals
 *
 * CTA behaviour:
 *   - Default: "Check it now" (auth-gated in-app navigation)
 *   - With `link` prop: renders via `ContentLink` (supports "View in Imagio"
 *     external links and "Check it now" with the same auth-gate)
 *   - No CTA at all when none of `link`/`searchUrl`/`productId` are set —
 *     prevents a dead "Check it now" that would navigate to unfiltered
 *     /search (CX-2790: same "hide when no valid target" rule ContentLink
 *     already applies to "view-in-imagio").
 */

import type { ContentLinkProps } from "components/content-link";
import { ContentLink } from "components/content-link";
import { useState } from "react";
import type { MaterialSample } from "../trendData";
import "./MaterialSampleCard.css";

interface MaterialSampleCardProps extends MaterialSample {
  checkItNowLabel?: string;
  /** When true, renders without border, shadow, and accent bar for use inside a shared-image card layout. */
  compact?: boolean;
}

export const MaterialSampleCard = ({
  image,
  colorHex,
  name,
  ralColor,
  material,
  shape,
  appearance,
  productId,
  searchUrl,
  link,
  checkItNowLabel = "Check it now",
  compact,
}: MaterialSampleCardProps) => {
  const [hovered, setHovered] = useState(false);

  const resolvedLink: ContentLinkProps | undefined = link
    ? link
    : searchUrl || productId
      ? { variant: "check-it-now", searchUrl, productId }
      : undefined;

  return (
    <article
      className={`material-sample-card${hovered ? " material-sample-card--hovered" : ""}${compact ? " material-sample-card--compact" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="material-sample-card__body">
        {/* Chip image — only if per-sample image is provided */}
        {image && (
          <div className="material-sample-card__image-wrap">
            <img
              src={image}
              alt={name}
              className="material-sample-card__image"
              loading="lazy"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3C/svg%3E";
              }}
            />
          </div>
        )}

        {/* Details */}
        <div className="material-sample-card__details">
          {/* Color swatch + name row */}
          <div className="material-sample-card__color-row">
            <span
              className="material-sample-card__swatch"
              style={{ backgroundColor: colorHex }}
              role="img"
              aria-label={`Color: ${name}`}
            />
            <div>
              <div className="material-sample-card__color-name">{name}</div>
            </div>
          </div>

          {/* Spec lines */}
          <div className="material-sample-card__specs">
            <p className="material-sample-card__spec-line">
              <strong>Color:</strong> {ralColor}
              <span className="material-sample-card__spec-sep" />
              <strong>Material:</strong> {material}
            </p>
            <p className="material-sample-card__spec-line">
              <strong>Opacity:</strong> {shape}
              <span className="material-sample-card__spec-sep" />
              <strong>Surface Texture:</strong> {appearance}
            </p>
          </div>

          {/* CTA — uses ContentLink for consistent behaviour; renders
              nothing when resolvedLink is undefined (no real target) */}
          {resolvedLink && (
            <ContentLink
              {...resolvedLink}
              label={
                resolvedLink.label ??
                (resolvedLink.variant === "check-it-now"
                  ? checkItNowLabel
                  : undefined)
              }
              className="material-sample-card__cta"
            />
          )}
        </div>
      </div>

      {/* Bottom accent bar — reveals on hover; omitted in compact mode */}
      {!compact && (
        <div className="material-sample-card__accent" aria-hidden="true" />
      )}
    </article>
  );
};
